/**
 * @module Interaction
 */

import GIS from "./app"
import DragAndDrop from "ol/interaction/DragAndDrop";
import Identify from "./interaction/identify"
import Hover from "./interaction/hover"
import DragAndDropGeoJSON from "./interaction/geojson"
import {pointerMove} from 'ol/events/condition';
import {GPX, IGC, KML, TopoJSON} from 'ol/format';
import {
    DoubleClickZoom,
    DragPan, DragRotate, DragZoom,
    KeyboardPan,
    KeyboardZoom,
    MouseWheelZoom,
    PinchRotate,
    PinchZoom,
    Select
} from "ol/interaction";
import {Interaction as olInteraction} from "ol/interaction";
import {MapConfiguration, InteractionOptions} from "./configuration/map";
import VectorSource, {VectorSourceEvent} from "ol/source/Vector";
import {MapBrowserEvent} from "ol";
import {StyleLike} from "ol/style/Style";
import {IdentifyCallback, HoverCallback, VertexHoverCallback} from "./general/callback";
import {IdentifyOptions, HoverOptions, VertexHoverOptions} from "./general/options";
import FeatureFormat from "ol/format/Feature";

class Interaction {

    private readonly GIS;

    /**
     * Array of controls
     */
    public entries:(Identify | Hover | Select | DragAndDrop)[] = [];
    private custom:{ [key: string]: Identify | Hover} = {};
    private openlayers:{ [key: string]: Select | DragAndDrop} = {};

    constructor(GIS:GIS) {
        this.GIS = GIS;
    }
    /**
     * Clear the Interaction object.
     * Useful, when you need to re-init the map component.
     * Deletes also interaction from the Map.
     */
    public clear () {
        this.custom = {};
        this.openlayers = {};
        this.entries.length = 0;
        if (this.GIS.Map.olObject) {
            this.GIS.Map.olObject!.getInteractions().clear();
        }
    }
    /**
     * Initialization of the Interaction object.
     * @param config - Layer configuration.
     */
    public init(config:MapConfiguration) {
        if (config.interactions) {
            config.interactions.map(interaction => {
                switch (interaction.type) {
                    case "openlayers":
                        this.create(interaction);
                        break;
                    case "custom":
                        this.create(interaction);
                        break;
                }
            });
        }
    };

    public getInteraction(name: string): olInteraction {
        let result;
        this.GIS.Map.olObject!.getInteractions().forEach(interaction => {
            switch (name) {
                case 'Identify':
                    if (interaction instanceof Identify) {result = interaction}
                    break;
                case 'Hover':
                    if (interaction instanceof Hover) {result = interaction}
                    break;
                case 'Select':
                    if (interaction instanceof Select) {result = interaction}
                    break;
                case 'DragPan':
                    if (interaction instanceof DragPan) {result = interaction}
                    break;
                case 'DoubleClickZoom':
                    if (interaction instanceof DoubleClickZoom) {result = interaction}
                    break;
                case 'PinchRotate':
                    if (interaction instanceof PinchRotate) {result = interaction}
                    break;
                case 'PinchZoom':
                    if (interaction instanceof PinchZoom) {result = interaction}
                    break;
                case 'KeyboardPan':
                    if (interaction instanceof KeyboardPan) {result = interaction}
                    break;
                case 'KeyboardZoom':
                    if (interaction instanceof KeyboardZoom) {result = interaction}
                    break;
                case 'MouseWheelZoom':
                    if (interaction instanceof MouseWheelZoom) {result = interaction}
                    break;
                case 'DragZoom':
                    if (interaction instanceof DragZoom) {result = interaction}
                    break;
                case 'DragRotate':
                    if (interaction instanceof DragRotate) {result = interaction}
                    break;
                case 'DragAndDrop':
                    if (interaction instanceof DragAndDrop) {result = interaction}
                    break;
            }
        });

        if (result) {
          return result;
        } else {
            throw new Error('Could not find the interaction.')
        }

    };
    /**
     * Activate or deactivate interaction
     * @param name Interaction name e.g. 'Identify'
     * @param isActive true/false
     * @throws Could not find the interaction.
     */
    public setActive(name: string, isActive: boolean) {
        const interaction = this.getInteraction(name);
        interaction.setActive(isActive);
    }

    private create(interaction: InteractionOptions) {
        switch (interaction.name) {
            case "Identify":
                this.custom[interaction.name] = new Identify(interaction, this.GIS);
                this.entries.push(this.custom[interaction.name]);
                break;
            case "Hover":
                this.custom[interaction.name] = new Hover(interaction, this.GIS);
                this.entries.push(this.custom[interaction.name]);
                break;
            case "DragAndDrop":
                const formats: Array<typeof FeatureFormat | FeatureFormat> = [];

                interaction.formats?.map( format => {
                    switch (format) {
                        case "GPX":
                            formats.push(GPX as typeof FeatureFormat);
                            break;
                        case "GeoJSON":
                            // Pre-constructed format with benevolent CRS detection —
                            // legacy/non-standard `crs` members and coordinate heuristics.
                            formats.push(new DragAndDropGeoJSON(() => this.GIS.View.olObject?.getProjection()));
                            break;
                        case "IGC":
                            formats.push(IGC as typeof FeatureFormat);
                            break;
                        case "KML":
                            formats.push(KML as typeof FeatureFormat);
                            break;
                        case "TopoJSON":
                            formats.push(TopoJSON as typeof FeatureFormat);
                            break;
                    }
                });

                this.openlayers[interaction.name] = new DragAndDrop({
                    formatConstructors: formats
                });

                const dragAndDrop = this.openlayers[interaction.name] as DragAndDrop;
                dragAndDrop.on("addfeatures", (event: any) => {
                    // Assert the event type inside the function
                    const vectorEvent = event as VectorSourceEvent;

                    if (interaction.layer) {
                        const source = this.GIS.Layer.getLayer(interaction.layer).getSource();
                        if (source && source instanceof VectorSource) {
                            const featuresToAdd = Array.isArray(vectorEvent.features) ? vectorEvent.features : [];
                            source.addFeatures(featuresToAdd);
                        }
                    }
                });

                this.entries.push(this.openlayers[interaction.name]);
                break;
            case "Select":
                let condition:(event: MapBrowserEvent<any>) => boolean;
                if (interaction.condition === "pointermove") {
                    condition = pointerMove;
                }

                interaction.layers?.map(layerDefinition => {
                    const name = interaction.name + '_' + layerDefinition.name;
                    this.openlayers[name] = new Select({
                        layers: (layer) => {
                            return layerDefinition.name === layer.get('name')
                        },
                        condition: condition,
                        hitTolerance: layerDefinition.hitTolerance,
                        style: this.GIS.Style.getStyle(layerDefinition.style!) as StyleLike
                    })

                    this.openlayers[name].set('name', name);

                    const inter = this.openlayers[name] as Select;
                    inter.on('select', event => {
                        this.GIS.Map.olObject!.getViewport().style.cursor = event.target.getFeatures().getLength() > 0 ? 'pointer' : '';
                    });

                    this.entries.push(this.openlayers[name]);
                })
                break;
        }
    }
    /**
     * Set interaction callback.
     * @param name - 'Identify' or 'Hover'
     * @param options - Options for the interaction
     * @param callback - Callback function
     */
    public setCallback(name: 'Identify', options?: IdentifyOptions, callback?: IdentifyCallback): void;
    public setCallback(name: 'Hover', options?: HoverOptions, callback?: HoverCallback): void;
    public setCallback(name: 'VertexHover', options?: VertexHoverOptions, callback?: VertexHoverCallback): void;
    public setCallback(name: string, options?: IdentifyOptions | HoverOptions | VertexHoverOptions, callback?: IdentifyCallback | HoverCallback | VertexHoverCallback): void {
        if (this.custom[name]) {
            (this.custom[name] as any).setCallback(options, callback);
        } else if (name === 'VertexHover') {
            const hover = this.custom['Hover'] as Hover | undefined;
            if (!hover) {
                console.warn('[Interaction.setCallback] No Hover interaction is registered. Add a custom Hover entry to your map configuration to enable vertex hover.');
                return;
            }
            hover.setVertexHoverCallback(options as VertexHoverOptions, callback as VertexHoverCallback);
        }
    }
}

export default Interaction;
